import { Card, Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, Box, Button, DataTable, FormField, ICodename, Modal, Typography, useCommonComponentContext } from "@maysoft/common-component-react";

import Helpers from "commons/helpers";
import Strings from "constants/strings";
import Screens from "constants/screens";
import Constants from "../../constants";
import DashboardLayout from "layout/dashboardLayout";
import SystemSettingService, { ISystemSetting, ISystemSettingPagingRequest } from "services/message/systemSetting.service";

import { setDataAlert } from "store/slice/message.slice";
import { showLoading } from "store/slice/loadingAPI.slice";
import { setListPathName } from "store/slice/titleRoute.slice";
import EmailConfigPopup from "./modal/emailConfigModal";
import TelegramConfigPopup from "./modal/telegramConfigModal";
import SimpleSettingPopup from "./modal/simpleConfigModal";
import TemplateConfigPopup from "./modal/casecodeConfigModal";
import FirebaseConfigPopup from "./modal/firebaseConfigModal";
import Add from "@mui/icons-material/Add";
import CreateSettingModal from "./modal/createModal";
import ServiceService from "services/identity/service.service";
import TenantService from "services/identity/tenant.service";
import NotificationPopup from "./modal/postNotifyModal";


interface ISystemSettingPagingModel {
    requestData?: ISystemSettingPagingRequest;
    requestDataTemp?: ISystemSettingPagingRequest;
}

const systemSettingService = new SystemSettingService();

const SettingScreen = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();

    const [model, setModel] = useState<ISystemSettingPagingModel>({
        requestData: {
            pageNumber: Number(searchParams.get("pageNumber") || 1),
            pageSize: Number(searchParams.get("pageSize") || Constants.ROW_PER_PAGE),

            serviceCode: searchParams.get("serviceCode"),
            tenantCode: searchParams.get("tenantCode"),
        },
        requestDataTemp: {
            pageNumber: Number(searchParams.get("pageNumber") || 1),
            pageSize: Number(searchParams.get("pageSize") || Constants.ROW_PER_PAGE),

            serviceCode: searchParams.get("serviceCode"),
            tenantCode: searchParams.get("tenantCode"),
        }
    } as ISystemSettingPagingModel);

    const [rows, setRows] = useState<ISystemSetting[]>([]);
    const [modal, setModal] = useState<boolean>(false);

    const [openCreate, setOpenCreate] = useState(false)

    const { language, userInfo } = useCommonComponentContext();
    const [currentSetting, setCurrentSetting] = useState<ISystemSetting>(null);

    useEffect(() => {
        getPaged()
    }, [userInfo?.userProfile?.organizationId]);

    const onChangeValue = (value: string | number, key: keyof ISystemSettingPagingRequest) => {
        setModel((prev) => ({ ...prev, requestDataTemp: { ...prev.requestDataTemp, [key]: value } }));
    };

    const onResetModel = () => setModel({ ...model, requestDataTemp: model.requestData });

    const SETTING_OPTIONS: ICodename[] = [
        { name: "Email Config", code: Constants.SettingCode.EMAIL_CONFIG },
        { name: "Firebase Config", code: Constants.SettingCode.FIREBASE_CONFIG },
        { name: "Telegram Config", code: Constants.SettingCode.TELEGRAM_CONFIG },
        { name: "Template Config", code: Constants.SettingCode.CASE_CODE },
        { name: "Post Notification Config", code: Constants.SettingCode.POST_NOTIFICATION },
        { name: "Online Time Terminate", code: Constants.SettingCode.ONLINE_TIME_TERMINATE },
        { name: "Schedule Job Fetch Time", code: Constants.SettingCode.SCHEDULE_JOB_FETCH_TIME }
    ]

    const getNameByCode = (code: string) => {
        return SETTING_OPTIONS.find(item => item.code === code)?.name || ""
    }

    const [services, setServices] = useState<ICodename[]>([]);
    const [tenants, setTenants] = useState<ICodename[]>([]);
    const [tenantMap, setTenantMap] = useState<Record<string, string>>({})
    const [serviceMap, setServiceMap] = useState<Record<string, string>>({})

    const getService = async () => {
        let currentServices: ICodename[] = [];
        const result = await new ServiceService().getAll();
        if (result) {
            currentServices = result.map(e => {
                return {
                    code: e?.serviceCode,
                    name: e?.serviceName
                }
            })
        }

        const map = Object.fromEntries(
            currentServices.map(item => [item.code, item.name])
        )
        setServiceMap(map)

        setServices(currentServices);
    }

    const getTenant = async () => {
        let currentTenants: ICodename[] = [];
        const result = await new TenantService().getAll();
        if (result) {
            currentTenants = result.map(e => {
                return {
                    code: e?.tenantCode,
                    name: e?.name
                }
            })
        }

        const map = Object.fromEntries(
            currentTenants.map(item => [item.code, item.name])
        )
        setTenantMap(map)

        setTenants(currentTenants);
    }


    const getPaged = async (newReq?: ISystemSettingPagingRequest) => {
        try {
            dispatch(showLoading(true));

            await getService();
            await getTenant();
            const pageSize = newReq?.pageSize || Constants.ROW_PER_PAGE;
            const pageNumber = Helpers.getPageNumber(newReq?.pageNumber || 1, pageSize, newReq?.totalCount || 0) || 1;
            const requestData: ISystemSettingPagingRequest = {
                pageSize: pageSize,
                pageNumber: pageNumber,

                tenantCode: newReq?.tenantCode || undefined,
                serviceCode: newReq?.serviceCode || undefined,
            };

            const resultGetPaged = await systemSettingService.getPaged(requestData);

            let query: string = `?pageNumber=${requestData.pageNumber}&pageSize=${requestData.pageSize}`;
            if (!Helpers.isNullOrEmpty(requestData?.serviceCode)) {
                query += `&serviceCode=${requestData?.serviceCode}`;
            }
            if (!Helpers.isNullOrEmpty(requestData?.tenantCode)) {
                query += `&tenantCode=${requestData?.tenantCode}`;
            }

            setRows(resultGetPaged.items);
            setModel({
                requestData: {
                    ...requestData,
                    totalCount: resultGetPaged.totalCount,
                },
                requestDataTemp: {
                    ...requestData,
                    totalCount: resultGetPaged.totalCount,
                },
            });
            dispatch(setListPathName({ pathname: Screens.SETTING_LIST, query, totalCount: resultGetPaged.totalCount }));
            navigate(Screens.SETTING_LIST + query, { replace: true });

        } catch (error) {
            const e = Helpers.renderExceptionError(error);
            dispatch(setDataAlert({ message: e, type: "error" }));
        } finally {
            dispatch(showLoading(false));
        }
    };

    const handleSubmit = async (value: string) => {
        try {
            showLoading(true);
            currentSetting.settingValue = value;
            const result = await systemSettingService.adminUpdate(currentSetting);

            if (result.statusCode === Constants.ApiCode.SUCCESS) {
                await getPaged();
            }

            setModal(false);
            setCurrentSetting(null);
            dispatch(setDataAlert({ message: Strings.Message.DELETE_SUCCESS, type: "success" }));
        }
        catch {

        }
        finally {
            showLoading(false);
        }
    }

    const handleClose = () => {
        setModal(false);
        setCurrentSetting(null);
    }

    const handleCloseCreateModal = () => {
        setOpenCreate(false);
        setCurrentSetting(null);
    }

    const handleCreateModalSubmit = async (data: ISystemSetting) => {
        try {
            showLoading(true);

            const result = await systemSettingService.create(data);

            if (result.statusCode === Constants.ApiCode.SUCCESS) {
                await getPaged();
            }

            setModal(false);
            setCurrentSetting(null);
            setOpenCreate(false);
            dispatch(setDataAlert({ message: Strings.Message.CREATE_SUCCESS, type: "success" }));
        }
        catch {

        }
        finally {
            showLoading(false);
        }
    }

    const columns = useMemo(() => {
        return [
            {
                Header: Strings.SETTING.TENANT, accessor: "tenantCode", width: "80px",
                Cell: (row: any) => {
                    return <span>{tenantMap[row?.value] || "-/-"}</span>;
                },
            },
            {
                Header: Strings.SETTING.SERVICE, accessor: "serviceCode", width: "80px",
                Cell: (row: any) => {
                    return <span>{serviceMap[row?.value] || "-/-"}</span>;
                },
            },
            {
                Header: Strings.SETTING.NAME, accessor: "id", width: "200px",
                Cell: (row: any) => {
                    return <span>{getNameByCode(row?.row?.original?.settingCode)}</span>;
                },

            },
            { Header: Strings.SETTING.CODE, accessor: "settingCode", width: "120px" },
        ];
    }, [tenants, language, Strings]);

    const simpleSetting: string[] = [Constants.SettingCode.SCHEDULE_JOB_FETCH_TIME, Constants.SettingCode.ONLINE_TIME_TERMINATE];

    const FilterForm = ({
        model,
        onChangeValue,
    }: {
        model: ISystemSettingPagingModel;

        onChangeValue: (value: string | number, key: keyof ISystemSettingPagingRequest) => void;
    }) => {

        return (
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Autocomplete
                            data={services}
                            label={Strings.SETTING.SERVICE}
                            placeholder={Strings.SETTING.SELECT_SERVICE}
                            defaultValue={model.requestDataTemp?.serviceCode || ""}
                            onChange={(value) => onChangeValue(value, "serviceCode")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Autocomplete
                            data={tenants}
                            label={Strings.SETTING.TENANT}
                            placeholder={Strings.SETTING.SELECT_TENANT}
                            defaultValue={model.requestDataTemp?.tenantCode || ""}
                            onChange={(value) => onChangeValue(value, "tenantCode")}
                        />
                    </Grid>
                </Grid>
            </Box>
        );
    };

    return (
        <DashboardLayout
            title={Strings.SETTING.SYSTEM_SETTING_LIST}
            route={[{ title: Strings.SETTING.SYSTEM_SETTING, route: "" }]}
        >
            <Box>

                <Box
                    sx={{
                        gap: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "end",
                    }}
                >
                    <Button onClick={() => setOpenCreate(true)}>
                        <Add /> &nbsp; {"Add"}
                    </Button>
                </Box>

                <Card sx={{ mt: 3 }}>
                    <Box p={2}>
                        <DataTable
                            // isShowIndex
                            pageNumber={1}
                            totalCount={rows.length || 0}
                            rowPerPage={Constants.ROW_PER_PAGE}
                            table={{
                                columns: columns,
                                rows: rows || [],
                            }}
                            filterForm={
                                <FilterForm
                                    {...{
                                        model,

                                        onChangeValue,
                                    }}
                                />
                            }
                            onFilter={() => getPaged({ ...model.requestDataTemp, pageNumber: 1 })}
                            onCloseFilter={onResetModel}
                            actionList={(row) => [
                                // {
                                //     key: "detail",
                                //     iconName: "view",
                                //     title: Strings.Common.VIEW_ALL,
                                //     callback: (row) => {
                                //         setCurrentSetting(row);
                                //         setModal(true);
                                //     }
                                // },
                                {
                                    key: "edit",
                                    iconName: "edit",
                                    title: Strings.Common.EDIT,
                                    callback: (row) => {
                                        setCurrentSetting(row);
                                        setModal(true);
                                    }
                                },
                            ]}
                        />

                        {modal && currentSetting?.settingCode === Constants.SettingCode.POST_NOTIFICATION && (
                            <NotificationPopup
                                serviceName={serviceMap[currentSetting?.tenantCode] || "-/-"}
                                tenantName={tenantMap[currentSetting?.tenantCode] || "-/-"}
                                visibled={modal}
                                onClose={handleClose}
                                data={currentSetting}
                                onSubmit={handleSubmit}
                            />
                        )}

                        {modal && currentSetting?.settingCode === Constants.SettingCode.FIREBASE_CONFIG && (
                            <FirebaseConfigPopup
                                serviceName={serviceMap[currentSetting?.tenantCode] || "-/-"}
                                tenantName={tenantMap[currentSetting?.tenantCode] || "-/-"}
                                visibled={modal}
                                onClose={handleClose}
                                value={currentSetting?.settingValue}
                                onSubmit={handleSubmit}
                            />
                        )}

                        {modal && currentSetting?.settingCode === Constants.SettingCode.EMAIL_CONFIG && (
                            <EmailConfigPopup
                                serviceName={serviceMap[currentSetting?.tenantCode] || "-/-"}
                                tenantName={tenantMap[currentSetting?.tenantCode] || "-/-"}
                                visibled={modal}
                                onClose={handleClose}
                                value={currentSetting?.settingValue}
                                onSubmit={handleSubmit}
                            />
                        )}

                        {modal && currentSetting?.settingCode === Constants.SettingCode.TELEGRAM_CONFIG && (
                            <TelegramConfigPopup
                                serviceName={serviceMap[currentSetting?.tenantCode] || "-/-"}
                                tenantName={tenantMap[currentSetting?.tenantCode] || "-/-"}
                                visibled={modal}
                                onClose={handleClose}
                                value={currentSetting?.settingValue}
                                onSubmit={handleSubmit}
                            />
                        )}

                        {modal && currentSetting?.settingCode === Constants.SettingCode.CASE_CODE && (
                            <TemplateConfigPopup
                                serviceName={serviceMap[currentSetting?.tenantCode] || "-/-"}
                                tenantName={tenantMap[currentSetting?.tenantCode] || "-/-"}
                                visibled={modal}
                                onClose={handleClose}
                                value={currentSetting?.settingValue}
                                onSubmit={handleSubmit}
                            />
                        )}

                        {modal && simpleSetting.includes(currentSetting.settingCode) && (
                            <SimpleSettingPopup
                                serviceName={serviceMap[currentSetting?.tenantCode] || "-/-"}
                                tenantName={tenantMap[currentSetting?.tenantCode] || "-/-"}
                                visibled={modal}
                                onClose={handleClose}
                                title={currentSetting?.settingCode}
                                value={currentSetting?.settingValue}
                                onSubmit={handleSubmit}
                            />
                        )}

                        {openCreate && (
                            <CreateSettingModal
                                services={services}
                                tenants={tenants}
                                visibled={openCreate}
                                onClose={handleCloseCreateModal}
                                onSubmit={handleCreateModalSubmit}
                            />
                        )}
                    </Box>
                </Card>
            </Box>
        </DashboardLayout>
    );
};





export default SettingScreen;
